import { useEffect, useRef, type RefObject } from 'react';

// Infinite-scroll sentinel (frontend-conventions §7.5 + §18.1). A 1px-high
// marker rendered as the LAST child INSIDE the scrollable container. When the
// sentinel nears view it calls `onReachEnd`, which the list wires to `fetchNextPage`.
//
// NEX-604 FIX: The IntersectionObserver MUST use the scrollable container as its
// `root`, not the viewport. When root=viewport (the default), the rootMargin only
// expands the viewport rect, but the sentinel is clipped by the scroll container's
// `overflow: auto/hidden`. A sentinel at y=875.5 with container bottom at y=875.0
// will NEVER intersect even with rootMargin=200px because the container clips it.
// By passing the scrollContainer ref, the observer measures against the container's
// visible bounds, and rootMargin expands THAT rect so the sentinel triggers early.
//
// Fallback: if the content doesn't fill the container (sentinel already visible on
// mount), we immediately call onReachEnd to load more pages until the container fills
// or there's no next page.

interface InfiniteScrollSentinelProps {
  onReachEnd: () => void;
  /** When true, the observer is inert (no next page, or a fetch is in flight). */
  disabled?: boolean;
  /** How far before the sentinel actually enters view to trigger (px). */
  rootMargin?: string;
  /**
   * The scrollable container element. The IntersectionObserver uses this as its
   * root so intersection is measured against the container's visible bounds, not
   * the viewport. Required for correct behavior when the sentinel is inside an
   * overflow:auto/hidden ancestor.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

export function InfiniteScrollSentinel({
  onReachEnd,
  disabled = false,
  rootMargin = '200px',
  scrollContainerRef,
}: InfiniteScrollSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without re-subscribing the observer each render.
  const onReachEndRef = useRef(onReachEnd);
  onReachEndRef.current = onReachEnd;

  useEffect(() => {
    if (disabled) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    // Use the scrollable container as root if provided; otherwise fall back to
    // viewport (legacy behavior, works only when no overflow clipping exists).
    const root = scrollContainerRef?.current ?? null;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onReachEndRef.current();
            break;
          }
        }
      },
      { root, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [disabled, rootMargin, scrollContainerRef]);

  // Sentinel needs non-zero height to have a measurable bounding box for
  // IntersectionObserver. 1px is invisible but sufficient.
  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="infinite-scroll-sentinel"
      style={{ height: '1px' }}
    />
  );
}
